import _isEmpty from "lodash/isEmpty";
import { isPOChangeDisabled } from "../PurchaseOrderDetail/utils";

export const SPEC_CATEGORY_CONFIRMATION_MESSAGE =
  "Updating Spec Category may affect the selected spec details and the invoices that they are part of. Are you sure of the change?";

/**
 * True when saving this payload would change the spec's category on a PO that is issued with
 * no change order in progress — the one case where the change leaves no trace in a CO log and
 * silently rebuilds the spec's default spec details, so the user is asked first.
 */
export const needsSpecCategoryConfirmation = (
  purchaseOrder,
  currentCategoryId,
  nextCategoryId
) =>
  nextCategoryId !== undefined &&
  nextCategoryId !== currentCategoryId &&
  isPOChangeDisabled(purchaseOrder);

// Merge onto formikBag.values (the whole form), not just submittedValues (the dirty batch
// that was withheld). Spec Details' AutoSave hands us the full Formik object — resetting
// with submittedValues alone would replace the entire form's initialValues/values with that
// one-or-two-key batch and blank every other field until a refetch recovers it. Quick Edit's
// FormikHelpers has no `values` key, so `formikBag.values` is undefined there and this
// degrades to exactly submittedValues plus the reverted category — today's behaviour.
//
// formikBag is only ever handed to us here when it can still do something useful with a
// reset — see the two call sites: Spec Details passes the live page formik (mounted
// throughout), Quick Edit omits it entirely because its container — and the Formik instance
// that produced this bag — is already unmounted by the time onCancel can run (the
// confirmation dialog replaced it in the single modal slot), so a reset there would only
// produce a "state update on an unmounted component" warning for no effect.
const revertCategoryOnForm = (
  formikBag,
  submittedValues,
  currentCategoryId
) => {
  if (!formikBag) return;

  formikBag.resetForm({
    values: {
      ...(formikBag.values || {}),
      ...submittedValues,
      specCategoryId: currentCategoryId,
    },
  });
};

// The category was withheld from the batch that was about to save. If anything else in
// that batch still needs to persist (e.g. AutoSave batched a description edit together
// with the category change), save it now with the category left out.
const saveBatchWithoutCategory = (onCancelSave, submittedValues) => {
  if (!onCancelSave || !submittedValues) return;

  const { specCategoryId, ...rest } = submittedValues;
  if (!_isEmpty(rest)) onCancelSave(rest);
};

const buildCancelHandler = (
  closeModalDialog,
  { formikBag, currentCategoryId, submittedValues, onCancelSave }
) => () => {
  revertCategoryOnForm(formikBag, submittedValues, currentCategoryId);
  saveBatchWithoutCategory(onCancelSave, submittedValues);
  closeModalDialog();
};

export const openSpecCategoryConfirmation = (
  { openModalDialog, closeModalDialog },
  { onConfirm, formikBag, currentCategoryId, submittedValues, onCancelSave }
) => {
  const onCancel = buildCancelHandler(closeModalDialog, {
    formikBag,
    currentCategoryId,
    submittedValues,
    onCancelSave,
  });

  openModalDialog(
    "Update Spec Category",
    "GeneralConfirmation",
    {
      title: SPEC_CATEGORY_CONFIRMATION_MESSAGE,
      sendButtonText: "Confirm",
      cancelButtonText: "Cancel",
      onSubmit: () => {
        onConfirm();
        closeModalDialog();
      },
      onCancel,
    },
    false,
    true,
    { onCloseModal: onCancel }
  );
};
